import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import Pill from '../components/Pill';
import { fmt, ago } from '../utils/format';
import { printInvoices } from '../utils/printInvoices';
import { RefreshButton } from '../hooks/useRefreshTrigger';
import ReceivedPOsSummaryPopup from './ReceivedPOsSummaryPopup';

// ── Truncate helper ───────────────────────────────────────────────────────────
const trunc = (str, len) => {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
};

// ── Stage pill colours ────────────────────────────────────────────────────────
const stageColor = (stage) => {
  if (!stage) return 'muted';
  const s = stage.toLowerCase();
  if (s.includes('fully picked'))      return 'green';
  if (s.includes('approved'))          return 'green';
  if (s.includes('new'))               return 'blue';
  if (s.includes('awaiting payment'))  return 'gold';
  if (s.includes('payment received'))  return 'gold';
  if (s.includes('warehouse'))         return 'blue';
  return 'muted';
};

// ── Dispatch status ───────────────────────────────────────────────────────────
// v_smart_buyer_dispatch shows an order the moment ANY of its outstanding
// items is covered by stock on hand, not just when ALL of them are -- an
// order straddling two suppliers (e.g. HOT + Toymod) can have half its
// lines ready to pick right now while the rest waits on a different
// supplier. order.order_ready (all items covered) tells FULL apart from
// PARTIAL_DISPATCHED; PARTIAL_READY covers the "some ready, some still
// waiting" case that the old all-or-nothing gate used to hide entirely.
const dispatchStatus = (order) => {
  if (!order.order_ready) return 'PARTIAL_READY';
  return order.items.some(i => Number(i.item_qty_moved) > 0) ? 'PARTIAL_DISPATCHED' : 'FULL';
};

// ── Grouped order table ─────────────────────────────────────────────────────
const SmartBuyerDispatchOrders = ({ orders, loading, selected, onToggle, onToggleAll }) => {
  const [expanded, setExpanded]   = useState(new Set());
  const [submitted, setSubmitted] = useState(new Set());

  if (loading) return <div className="loading-row">Loading...</div>;
  if (!orders.length) return <div className="empty-row">✓ No Smart Buyer orders with anything ready to pick</div>;

  const allSelected  = orders.every(o => selected.has(o.order_ref));
  const someSelected = orders.some(o => selected.has(o.order_ref));

  const toggleExpand = (orderRef) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(orderRef)) next.delete(orderRef); else next.add(orderRef);
      return next;
    });
  };

  const handleFDHold = async (e, orderRef) => {
    e.stopPropagation();
    try {
      await supabase.from('sales_order_stage_updates').insert({
        order_ref: orderRef,
        stage:     'Fully Picked - Hold',
      });
      setSubmitted(prev => new Set(prev).add(orderRef));
    } catch (err) {
      alert(`Failed to queue ${orderRef}: ${err.message}`);
    }
  };

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: 36, textAlign: 'center' }}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                onChange={() => onToggleAll(orders)}
                title="Select all"
                style={{ cursor: 'pointer' }}
              />
            </th>
            <th style={{ width: 24 }}></th>
            <th>Order Ref</th>
            <th style={{ width: 90 }}></th>
            <th>Status</th>
            <th>Stage</th>
            <th>Customer</th>
            <th>Supplier(s)</th>
            <th>Received PO</th>
            <th>Received</th>
            <th className="num">Lines</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const isSelected  = selected.has(order.order_ref);
            const isExpanded  = expanded.has(order.order_ref);
            const isSubmitted = submitted.has(order.order_ref);
            const isOnHold    = order.stage === 'Fully Picked - Hold';
            const status      = dispatchStatus(order);
            // Holding only makes sense once the whole order is coverable --
            // holding a partially-ready order would also block the items
            // that are genuinely ready to pick right now.
            const isDispatchable = order.order_ready && !isOnHold;

            return (
              <React.Fragment key={order.order_ref}>
                <tr
                  className={`data-row ${isSelected ? 'data-row--selected' : ''} ${isExpanded ? 'data-row--expanded' : ''}`}
                  onClick={() => toggleExpand(order.order_ref)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggle(order.order_ref)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 11 }}>
                    {isExpanded ? '▼' : '▶'}
                  </td>
                  <td><span className="code-badge">{order.order_ref}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    {isDispatchable && (
                      isSubmitted
                        ? <span style={{ fontSize: 10, color: 'var(--muted)', fontStyle: 'italic' }}>Submitted</span>
                        : <button
                            onClick={e => handleFDHold(e, order.order_ref)}
                            style={{
                              fontSize: 10, padding: '2px 8px', cursor: 'pointer',
                              background: 'rgba(148,103,60,0.25)', color: '#c8922a',
                              border: '1px solid rgba(200,146,42,0.35)', borderRadius: 4, fontWeight: 600,
                              letterSpacing: '0.03em',
                            }}
                          >
                            FPHold
                          </button>
                    )}
                  </td>
                  <td>
                    {status === 'FULL'              && <span className="dispatch-full">FULL · DISPATCH</span>}
                    {status === 'PARTIAL_DISPATCHED' && <span className="dispatch-partial-dispatched">PARTIAL · DISPATCH</span>}
                    {status === 'PARTIAL_READY'      && <span className="dispatch-partial">PARTIAL · READY</span>}
                  </td>
                  <td><Pill label={order.stage || '—'} color={stageColor(order.stage)} /></td>
                  <td>{trunc(order.customer_name, 20)}</td>
                  <td>
                    {order.suppliers.map(s => (
                      <Pill key={s} label={trunc(s, 10)} color="blue" />
                    ))}
                  </td>
                  <td>
                    {order.po_refs.map(ref => (
                      <span key={ref} className="code-badge code-badge--sm" style={{ marginRight: 3 }}>{ref}</span>
                    ))}
                  </td>
                  <td className="muted">{ago(order.fully_received_date)}</td>
                  <td className="num">{order.items.length}</td>
                </tr>

                {isExpanded && (
                  <tr className="detail-row">
                    <td colSpan={11} style={{ padding: 0 }}>
                      <div className="order-items-detail">
                        <table className="detail-table">
                          <thead>
                            <tr>
                              <th className="col-code">Item Code</th>
                              <th style={{ width: 90, textAlign: 'center' }}>Smart Buyer</th>
                              <th className="col-name">Item Name</th>
                              <th className="col-ordered num">Ordered</th>
                              <th className="col-moved num">Moved</th>
                              <th className="col-avail num">Stock On Hand</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.items.map((item, i) => (
                              <tr key={i}>
                                <td className="col-code"><span className="code-badge code-badge--sm">{item.item_code}</span></td>
                                <td style={{ textAlign: 'center' }}>
                                  {item.is_smart_buyer_linked && <span className="pill pill--blue" style={{ fontSize: 10 }}>Linked</span>}
                                </td>
                                <td className="col-name product-name">{trunc(item.item_name, 40)}</td>
                                <td className="col-ordered num">{fmt(item.item_qty)}</td>
                                <td className="col-moved num">{fmt(item.item_qty_moved)}</td>
                                <td className={`col-avail num ${item.item_ready ? '' : 'neg'}`}>{fmt(item.stock_on_hand)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── SmartBuyerDispatch Page ────────────────────────────────────────────────────
const SmartBuyerDispatch = () => {
  const navigate = useNavigate();
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [supplier, setSupplier]       = useState('');
  const [selected, setSelected]       = useState(new Set());
  const [printing, setPrinting]       = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    const { data: rows } = await supabase
      .from('v_smart_buyer_dispatch')
      .select('*')
      .order('order_created_date', { ascending: true });
    setData(rows || []);
    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of data) {
      if (!map.has(row.order_ref)) {
        map.set(row.order_ref, {
          order_ref:           row.order_ref,
          stage:               row.stage,
          customer_name:       row.customer_name,
          order_created_date:  row.order_created_date,
          order_ready:         row.order_ready,
          fully_received_date: row.fully_received_date,
          suppliers:           new Set(),
          po_refs:             new Set(),
          items:               [],
        });
      }
      const entry = map.get(row.order_ref);
      if (row.supplier_name) entry.suppliers.add(row.supplier_name);
      // The PO that brought the stock in, not the Smart Buyer PO that
      // ordered it -- those differ (HC99545/TAM18105 was ordered on PO-383537
      // and received on PO-383726), and it is the receiving PO staff are
      // reconciling against when picking.
      if (row.last_received_po) entry.po_refs.add(row.last_received_po);
      // Multiple lines can each carry their own last-received PO/date; show
      // the most recent receipt, since that's the one that just made the
      // order dispatchable.
      if (row.fully_received_date &&
          (!entry.fully_received_date || row.fully_received_date > entry.fully_received_date)) {
        entry.fully_received_date = row.fully_received_date;
      }
      entry.items.push({
        item_code:              row.item_code,
        item_name:              row.item_name,
        item_qty:               row.item_qty,
        item_qty_moved:         row.item_qty_moved,
        stock_on_hand:          row.stock_on_hand,
        item_ready:             row.item_ready,
        is_smart_buyer_linked:  row.is_smart_buyer_linked,
      });
    }
    return [...map.values()].map(o => ({
      ...o,
      suppliers: [...o.suppliers],
      po_refs:   [...o.po_refs],
    }));
  }, [data]);

  const suppliers = useMemo(() =>
    [...new Set(data.map(r => r.supplier_name).filter(Boolean))].sort()
  , [data]);

  const filtered = useMemo(() =>
    !supplier ? grouped : grouped.filter(o => o.suppliers.includes(supplier))
  , [grouped, supplier]);

  const handleToggle = (orderRef) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(orderRef)) next.delete(orderRef); else next.add(orderRef);
      return next;
    });
  };

  const handleToggleAll = (orders) => {
    const allSelected = orders.every(o => selected.has(o.order_ref));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) orders.forEach(o => next.delete(o.order_ref));
      else orders.forEach(o => next.add(o.order_ref));
      return next;
    });
  };

  const handlePrint = async () => {
    if (!selected.size) return;
    setPrinting(true);
    await printInvoices([...new Set([...selected])]);
    setPrinting(false);
  };

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/dispatch-tools')}>← Dispatch Tools</button>
        <span className="page-nav__title">Smart Buyer — Ready to Dispatch</span>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button
          onClick={() => setShowSummary(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', whiteSpace: 'nowrap',
            background: 'var(--bg3, #f0f2f5)',
            border: '1px solid var(--border2, #d0d5dd)',
            color: 'var(--text, #1a1a2e)',
          }}
        >
          Show Received POs
        </button>
        <RefreshButton triggerKey={['purchase_orders', 'stock_counts']} label="Refresh" onDone={load} />
      </div>
      {showSummary && <ReceivedPOsSummaryPopup onClose={() => setShowSummary(false)} />}

      <section className="section">
        <SectionHeader
          title="Smart Buyer Orders — Ready to Dispatch"
          count={filtered.length}
          onRefresh={load}
          loading={loading}
          actions={
            <>
              <button
                className="action-btn action-btn--close"
                onClick={handlePrint}
                disabled={printing || selected.size === 0}
                style={{ marginRight: 8, opacity: selected.size === 0 ? 0.4 : 1 }}
              >
                {printing ? '…' : `🖨 Print${selected.size > 0 ? ` ${selected.size} Invoice${selected.size !== 1 ? 's' : ''}` : ''}`}
              </button>
              <select
                value={supplier}
                onChange={e => { setSupplier(e.target.value); setSelected(new Set()); }}
                style={{
                  background: 'var(--bg3)', color: 'var(--white)',
                  border: '1px solid var(--border2)', borderRadius: 4,
                  padding: '4px 10px', fontFamily: 'var(--font-body)',
                  fontSize: 12, cursor: 'pointer', marginRight: 8,
                }}
              >
                <option value="">All Suppliers</option>
                {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </>
          }
        />
        <p className="section__desc">
          Sales orders with an open Smart Buyer need where at least one outstanding item — Smart
          Buyer linked or not — is now covered by current stock. FULL means everything's ready;
          PARTIAL · READY means some items can be picked now while the rest waits on a different
          supplier. Oldest order wins when more than one open Smart Buyer order is waiting on the
          same item code. Click a row to see items.
          {supplier && <strong style={{ color: 'var(--gold)', marginLeft: 8 }}>Filtered: {supplier}</strong>}
          {selected.size > 0 && <strong style={{ color: 'var(--green-light)', marginLeft: 8 }}>{selected.size} selected</strong>}
        </p>
        <SmartBuyerDispatchOrders
          orders={filtered}
          loading={loading}
          selected={selected}
          onToggle={handleToggle}
          onToggleAll={handleToggleAll}
        />
      </section>
    </main>
  );
};

export default SmartBuyerDispatch;
